/* Sign in, on a phone — the design's own sign-in screen against the real
 * session.
 *
 * The design accepted any username and password and went straight to home;
 * this posts to /api/auth/login like the desktop page does, so the same
 * three accounts and the same HttpOnly cookie carry the phone. Nothing is
 * remembered in the page: the session is the cookie.
 */

import { useState } from 'react'
import { ApiError } from '../lib/api'
import { auth, type User } from '../lib/auth'
import { useIsDark, useTheme } from '../lib/theme'
import './mobile.css'
import { BpButton } from './parts'
import { Blueprint } from '../components/ui'

export function MobileSignIn({ onSignedIn }: { onSignedIn: (u: User) => void }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const dark = useIsDark()
  const [, setTheme] = useTheme()

  const submit = async () => {
    if (!username.trim() || !password) {
      setError('Enter your username and password.')
      return
    }
    setBusy(true)
    try {
      onSignedIn(await auth.login(username.trim(), password))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reach the server.')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="m-app">
      <form
        onSubmit={(e) => { e.preventDefault(); void submit() }}
        style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          padding: 'calc(36px + env(safe-area-inset-top)) 24px calc(44px + env(safe-area-inset-bottom))',
        }}
      >
        <div className="m-eyebrow">Mohans Sri Chand Tex</div>
        <h1 style={{ fontSize: 48, lineHeight: 0.95, margin: '10px 0' }}>
          Mill<br />dashboards
        </h1>
        <p className="m-sub" style={{ margin: 0, maxWidth: 290, lineHeight: 1.5 }}>
          Sign in with your mill portal account.
        </p>

        <Blueprint style={{
          marginTop: 36, padding: '20px 18px',
          display: 'flex', flexDirection: 'column', gap: 16,
        }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="m-label" style={{ color: 'var(--color-neutral-700)' }}>Username</span>
            <input
              className="input" autoComplete="username" autoCapitalize="none"
              autoCorrect="off" value={username}
              onChange={(e) => { setUsername(e.target.value); setError('') }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span className="m-label" style={{ color: 'var(--color-neutral-700)' }}>Password</span>
            <input
              className="input" type="password" autoComplete="current-password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError('') }}
            />
          </label>
          {error ? (
            <div style={{ fontSize: 13, color: 'var(--c-bad)' }} role="alert">{error}</div>
          ) : null}
          {/* A submit button inside the form, so the keyboard's Go key works
              as well as the tap. */}
          <BpButton onClick={() => void submit()} style={{
            height: 50, border: 0, background: 'var(--color-accent)', color: '#fff',
            font: '600 18px var(--font-heading)', letterSpacing: '0.02em',
            cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
          }}>{busy ? 'Signing in…' : 'Sign in'}</BpButton>
          <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
        </Blueprint>

        <div style={{
          marginTop: 'auto', display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', fontSize: 12, color: 'var(--color-neutral-600)',
        }}>
          <span>Data refreshes as files are loaded</span>
          <button type="button" className="m-btn"
            onClick={() => setTheme(dark ? 'light' : 'dark')}>
            {dark ? 'Light' : 'Dark'}
          </button>
        </div>
      </form>
    </div>
  )
}
